/**
 * GA4 (Google Analytics 4) — Hérita
 *
 * The gtag.js loader and the initial `config` call live in
 * /app/frontend/public/index.html so they are part of every HTML response
 * (including the legal pages served straight out of the SPA shell).
 *
 * This module exposes thin, named wrappers that match GA4's recommended
 * event names so the data lines up with the standard reports
 * (Monetization, Engagement, etc.):
 *
 *   - file_upload     → document successfully uploaded
 *   - view_item       → user opens an analysis (free or premium)
 *   - begin_checkout  → user clicks "Débloquer ma lecture"
 *   - purchase        → Stripe payment confirmed
 *   - generate_lead   → user creates an account (auth not shipped yet)
 *
 * Every wrapper is safe to call before gtag.js is ready (no-op fallback)
 * and never throws, so a failing GA load never breaks the product flow.
 */

const isBrowser = typeof window !== 'undefined';

const safeGtag = (...args) => {
  if (!isBrowser) return;
  try {
    if (typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  } catch {
    /* analytics must never break the app */
  }
};

/** Generic passthrough — use only for one-off events. */
export const track = (eventName, params = {}) => {
  if (!eventName) return;
  safeGtag('event', eventName, params);
};

/* ----------- Domain events ----------- */

/** Fired after a document is successfully uploaded to /api/documents/upload. */
export const trackFileUpload = ({ documentId, fileType, fileName, fileSize } = {}) => {
  safeGtag('event', 'file_upload', {
    document_id: documentId,
    file_type: fileType,
    file_name: fileName,
    file_size: fileSize,
  });
};

/** Fired when an analysis is opened in the dashboard. */
export const trackViewItem = ({ documentId, documentType, isPremium } = {}) => {
  safeGtag('event', 'view_item', {
    currency: 'EUR',
    value: 19,
    items: [
      {
        item_id: documentId || 'unknown',
        item_name: 'Dossier Hérita Premium',
        item_category: documentType || 'document_notarial',
        price: 19,
        quantity: 1,
      },
    ],
    is_premium: Boolean(isPremium),
  });
};

/** Fired when the user clicks "Débloquer ma lecture" (entry into the checkout flow). */
export const trackBeginCheckout = ({ documentId, documentType } = {}) => {
  safeGtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: 19,
    items: [
      {
        item_id: documentId || 'unknown',
        item_name: 'Dossier Hérita Premium',
        item_category: documentType || 'document_notarial',
        price: 19,
        quantity: 1,
      },
    ],
  });
};

/**
 * Fired on /paiement-succes once Stripe confirms payment_status === 'paid'.
 *
 * IMPORTANT — GA4 does NOT deduplicate purchase events server-side. We must
 * guarantee a single emission per transaction_id ourselves. We persist the
 * sent transaction_id list in localStorage so that a page reload, a Stripe
 * webhook retry, or a Strict-Mode double-effect never produces a duplicate
 * `purchase` event in GA4.
 *
 * Recommended params follow Google's Ecommerce reference:
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events#purchase
 */
const PURCHASE_DEDUPE_KEY = 'herita_ga4_purchase_sent_v1';
const PURCHASE_DEDUPE_MAX = 50; // cap to avoid unbounded growth

const readSentPurchases = () => {
  if (!isBrowser) return [];
  try {
    const raw = window.localStorage.getItem(PURCHASE_DEDUPE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const rememberSentPurchase = (transactionId) => {
  if (!isBrowser || !transactionId) return;
  try {
    const next = [...readSentPurchases().filter((id) => id !== transactionId), transactionId];
    // Keep only the last N to avoid quota issues
    const trimmed = next.slice(-PURCHASE_DEDUPE_MAX);
    window.localStorage.setItem(PURCHASE_DEDUPE_KEY, JSON.stringify(trimmed));
  } catch {
    /* localStorage may be blocked — fail silently */
  }
};

export const hasPurchaseBeenTracked = (transactionId) =>
  Boolean(transactionId) && readSentPurchases().includes(transactionId);

export const trackPurchase = ({
  sessionId,
  documentId,
  value,
  currency,
  packageId,
} = {}) => {
  if (!sessionId) return false;
  if (hasPurchaseBeenTracked(sessionId)) return false;

  const numericValue = Number.isFinite(Number(value)) ? Number(value) : 19;
  const safeValue = Math.round(numericValue * 100) / 100;
  const safeCurrency = (currency || 'EUR').toUpperCase();

  safeGtag('event', 'purchase', {
    transaction_id: sessionId,
    value: safeValue,
    currency: safeCurrency,
    tax: 0,
    shipping: 0,
    affiliation: 'Hérita',
    items: [
      {
        item_id: packageId || 'herita_dossier_premium',
        item_name: 'Dossier Hérita Premium',
        item_category: 'document_notarial',
        item_brand: 'Hérita',
        item_variant: documentId || 'unknown',
        price: safeValue,
        quantity: 1,
      },
    ],
  });
  rememberSentPurchase(sessionId);
  return true;
};

/**
 * Fired when a user creates an account.
 * NOTE: Auth is not shipped yet — this helper is ready for the future
 * JWT / Google OAuth integration. Call it from the signup success handler.
 */
export const trackGenerateLead = ({ method = 'email' } = {}) => {
  safeGtag('event', 'generate_lead', {
    method,
    currency: 'EUR',
    value: 19,
  });
};

/* ---------- Free diagnostic funnel (Feb 2026) ---------- */

/** Click on the "Faire un diagnostic gratuit" CTA from the homepage. */
export const trackDiagnosticCtaClicked = (source = 'home_hero') => {
  safeGtag('event', 'diagnostic_cta_clicked', { source });
};

/** First step actually rendered on /diagnostic. */
export const trackDiagnosticStarted = () => {
  safeGtag('event', 'diagnostic_started', {});
};

/** Each time the user moves to the next step (1..4). */
export const trackDiagnosticStepCompleted = (stepIndex, stepName) => {
  safeGtag('event', 'diagnostic_step_completed', {
    step_index: stepIndex,
    step_name: stepName,
  });
};

/** Result screen displayed after the rule engine returned. */
export const trackDiagnosticCompleted = ({ mainGoal, hasChildren, familyStatus } = {}) => {
  safeGtag('event', 'diagnostic_completed', {
    main_goal: mainGoal || 'unknown',
    has_children: Boolean(hasChildren),
    family_status: familyStatus || 'unknown',
  });
};

/** Email captured at the bottom of the result screen. */
export const trackDiagnosticEmailSubmitted = () => {
  safeGtag('event', 'diagnostic_email_submitted', {});
};

/** Click on the "Analyser mon document" CTA at the end of the diagnostic. */
export const trackDiagnosticToUploadClicked = () => {
  safeGtag('event', 'diagnostic_to_upload_clicked', {});
};
